import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Home from './homepage/Home';
import IMatrixComponent from './hero/IMatrixComponent';
import TallyPrime from './tallyprime/TallyPrime';
import TallyShowcase from './tallyprime/TallyShowcase';
import Footer from './footer/Footer';
import Timeline from './timelines/Timeline';
import Content_New from './content_new/Content_New';
import Client from './Client/Client';
import AboutPage from './AboutPage/AboutPage';
import HardwareServices from './HardwareServices/HardwareServices';
import SecuritySystem from './SecuritySystem/SecuritySystem';
import MobileDeveloper from './MobileDeveloper/MobileDeveloper';
import WebDevelopment from './WebDevelopment/WebDevelopment';
import ServicePage from './ServicePage/ServicePage';
import ServerPage from './ServerPage/ServerPage';
import ProductGrid from './BIO/ProductGrid';
import Antivirus from './AntivirusF/Antivirus';
import AutoBackupSoftware from './autoBackupSoftware/AutoBackupSoftware';
import PrinterSoftware from './PrinterSoftware/PrinterSoftware';
import WebList from './webcat/WebList';
import EWarehouse from './e_data/EWarehouse';
import PRPurchaseRequest from './pr/PRPurchaseRequest';
import TransportWebApplication from './tr/TransportWebApplication';
import './App.css';
import Header from './homepage/Header';
import SplashScreen from './components/SplashScreen';
import ScrollToTop from './ScrollToTop';
import NotFound from './NotFound';
import CareerPage from './careers_pages/CareerPage';
import SuperAdmin from './admin/SuperAdmin';
import ContactAdmin from './admin/ContactAdmin';
import PageHitsAdmin from './admin/PageHitsAdmin';
import AdminLogin from './admin/AdminLogin';
import AdminGuard from './admin/AdminGuard';
import TallySoftware from './tallyprime/tallysoft/TallySoftware';
import TallyPrimeServer from './tallyprime/tallyprime/TallyPrimeServer';
import TallyAws from './tallyprime/tallyprimeaws/TallyAws';
import WhatsAppBusiness from './tallyprime/whatapp/WhatsAppBusiness';
import TallyPlatform from './tallyprime/custom/TallyPlatform';
import TallyDefinition from './tallyprime/TDL/TallyDefinition';
import PrimeDetails from './tallyprime/prime/PrimeDetails';
import TallyDetails from './tallyprime/ERP/TallyDetails';
import BizAnalyst from './tallyprime/analyst/BizAnalyst';
import PlansAndPricing from './tallyprime/analyst/PlansAndPricing';

import OurService from './tallyprime/tallyourservice/OurService';
import InventoryManagement from './tallyprime/tallyprimefeildspages/InventoryManagement';
import ReportDesign from './tallyprime/tallyprimefeildspages/ReportDesign';
import PaymentManagement from './tallyprime/tallyprimefeildspages/PaymentManagement';
import Taxation from './tallyprime/tallyprimefeildspages/Taxation';
import InvoiceFeature from './tallyprime/tallyprimefeildspages/InvoiceFeature';
import BankReconciliation from './tallyprime/tallyprimefeildspages/BankReconciliation';
import ConnectedServices from './tallyprime/tallyprimefeildspages/ConnectedServices';
import Plans from './tallyprime/tallyprimefeildspages/plans'; 
import TallyPrimePlan from './tallyprime/tallyprimefeildspages/TallyPrimePlan'; 
import SolutionsPage from './solutions/SolutionsPage';
import ChatBot from './components/ChatBot/ChatBot';
import PageTracker from './components/PageTracker';
// import SiteTour from './components/SiteTour/SiteTour'; // Interactive Guided Tour (disabled for now)
import WhatsNewPage from './whats-new/WhatsNewPage';
import WhatsNewAdmin from './admin/WhatsNewAdmin';
import TallyIra from './tallyprime/tallyira/TallyIra';
import ConnectSocials from './connect/ConnectSocials';

/* ─────────────────────────────────────────────────────────────
   MatrixRain — iMatrix-branded full-screen digital rain canvas
   Triggered: click the footer logo
───────────────────────────────────────────────────────────── */
const MATRIX_CHARS = '01アイウエオカキクケコサシスセソタチツテトナニヌネノiMatrix2006300020IM';

const MatrixRain = ({ onDone }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animId;
    let opacity = 0;
    let phase = 'in'; // 'in' | 'hold' | 'out'
    let holdTimer;

    const resize = () => {
      canvas.width  = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const FONT_SIZE = 16;
    const cols = Math.floor(canvas.width / FONT_SIZE);
    const drops = Array.from({ length: cols }, () => Math.random() * -canvas.height / FONT_SIZE);

    // Brand words that occasionally appear in columns
    const brandWords = ['iMatrix', '2006', '3000+', 'Tally', 'Chennai', '5Star', 'ITPvtLtd'];
    const columnWord = Array(cols).fill(null);
    const columnWordIdx = Array(cols).fill(0);

    // Assign random brand words to ~15% of columns
    for (let i = 0; i < cols; i++) {
      if (Math.random() < 0.15) {
        columnWord[i] = brandWords[Math.floor(Math.random() * brandWords.length)];
      }
    }

    const draw = () => {
      ctx.fillStyle = `rgba(0, 0, 0, 0.05)`;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      for (let i = 0; i < cols; i++) {
        let char;
        if (columnWord[i] && columnWordIdx[i] < columnWord[i].length) {
          char = columnWord[i][columnWordIdx[i]];
          columnWordIdx[i]++;
        } else {
          columnWordIdx[i] = 0;
          char = MATRIX_CHARS[Math.floor(Math.random() * MATRIX_CHARS.length)];
        }

        const y = drops[i] * FONT_SIZE;

        // Lead character — bright white/gold for brand words, else bright green
        const isBrand = columnWord[i] && columnWordIdx[i] <= columnWord[i].length;
        ctx.fillStyle = isBrand ? `rgba(255, 215, 0, ${opacity})` : `rgba(180, 255, 180, ${opacity})`;
        ctx.font = `bold ${FONT_SIZE}px 'Courier New', monospace`;
        ctx.fillText(char, i * FONT_SIZE, y);

        // Trail — dim green
        ctx.fillStyle = `rgba(0, 200, 60, ${opacity * 0.7})`;
        ctx.font = `${FONT_SIZE}px 'Courier New', monospace`;

        if (drops[i] * FONT_SIZE > canvas.height && Math.random() > 0.975) {
          drops[i] = 0;
          if (Math.random() < 0.15) {
            columnWord[i] = brandWords[Math.floor(Math.random() * brandWords.length)];
          } else {
            columnWord[i] = null;
          }
          columnWordIdx[i] = 0;
        }
        drops[i] += 0.5;
      }
    };

    const FADE_SPEED = 0.025;
    const HOLD_MS    = 8000;

    const animate = () => {
      if (phase === 'in') {
        opacity = Math.min(opacity + FADE_SPEED, 1);
        if (opacity >= 1) {
          phase = 'hold';
          holdTimer = setTimeout(() => { phase = 'out'; }, HOLD_MS);
        }
      } else if (phase === 'out') {
        opacity = Math.max(opacity - FADE_SPEED * 0.6, 0);
        if (opacity <= 0) {
          cancelAnimationFrame(animId);
          onDone && onDone();
          return;
        }
      }
      draw();
      animId = requestAnimationFrame(animate);
    };

    animId = requestAnimationFrame(animate);
    return () => {
      cancelAnimationFrame(animId);
      clearTimeout(holdTimer);
      window.removeEventListener('resize', resize);
    };
  }, [onDone]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 999996,
        pointerEvents: 'none',
        background: 'transparent',
      }}
      aria-hidden="true"
    />
  );
};

function App() {
  const [splashState, setSplashState] = useState(() => {
    if (typeof window === 'undefined') return 'completed';
    const isDebug = new URLSearchParams(window.location.search).get('debugSplash') === 'true';
    const isHome = window.location.pathname === '/';
    const initialShown = sessionStorage.getItem('splashShown') === 'true';
    return isDebug ? 'loading' : (isHome && !initialShown ? 'loading' : 'completed');
  });

  // ── Easter Egg: Konami Code ──────────────────────────────
  const [konamiActive, setKonamiActive] = useState(false);
  const konamiSequence = ['arrowup','arrowup','arrowdown','arrowdown','arrowleft','arrowright','arrowleft','arrowright','b','a'];
  const konamiProgress = useRef([]);

  // ── Easter Egg: Matrix Mode (type "imatrix") ─────────────
  const [matrixMode, setMatrixMode] = useState(false);
  const matrixTarget = 'imatrix';
  const matrixProgress = useRef('');
  const matrixTimer = useRef(null);

  // ── Easter Egg: Matrix Digital Rain (click footer logo 5 times) ──
  const [matrixRain, setMatrixRain] = useState(false);
  const matrixRainTimer = useRef(null);
  const matrixRainClicks = useRef(0);
  const matrixRainClickTimer = useRef(null);

  const triggerMatrixRain = useCallback(() => {
    matrixRainClicks.current += 1;
    clearTimeout(matrixRainClickTimer.current);
    
    if (matrixRainClicks.current >= 5) {
      matrixRainClicks.current = 0;
      clearTimeout(matrixRainTimer.current);
      setMatrixRain(true);
      matrixRainTimer.current = setTimeout(() => setMatrixRain(false), 12000);
    } else {
      matrixRainClickTimer.current = setTimeout(() => {
        matrixRainClicks.current = 0;
      }, 800);
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const shouldShowDevHint =
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1' ||
      params.get('debug') === 'easteregg';

    if (shouldShowDevHint && typeof console !== 'undefined') {
      console.info('Psst… try ↑ ↑ ↓ ↓ ← → ← → B A — and also type "imatrix" or click the footer logo 5 times.');
    }

    const handleKeyDown = (e) => {
      // Skip if user is typing in an input/textarea
      const tag = e.target.tagName;
      const isInput = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || e.target.isContentEditable;
      const key = e.key.toLowerCase();

      // ── Konami Code tracker ──
      konamiProgress.current = [...konamiProgress.current, key].slice(-konamiSequence.length);
      if (konamiProgress.current.join(',') === konamiSequence.join(',')) {
        setKonamiActive(true);
        konamiProgress.current = [];
        setTimeout(() => setKonamiActive(false), 4000);
      }

      // ── Matrix mode tracker (only if not typing in form fields) ──
      if (!isInput) {
        clearTimeout(matrixTimer.current);
        matrixProgress.current = (matrixProgress.current + e.key).slice(-matrixTarget.length);
        if (matrixProgress.current === matrixTarget) {
          setMatrixMode(true);
          matrixProgress.current = '';
          clearTimeout(matrixTimer.current);
          matrixTimer.current = setTimeout(() => setMatrixMode(false), 8000);
        }
        // Reset progress if no key pressed for 2s
        matrixTimer.current = setTimeout(() => { matrixProgress.current = ''; }, 2000);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Apply / remove matrix mode class on body
  useEffect(() => {
    if (matrixMode) {
      document.body.classList.add('matrix-mode-active');
    } else {
      document.body.classList.remove('matrix-mode-active');
    }
  }, [matrixMode]);


  // Refs for source (splash badge img) and destination (header logo slot)
  const splashLogoRef = useRef(null);
  const headerLogoRef = useRef(null);

  // flyState: null | { x, y, w, h, tx, ty, tw, th }
  const [flyState, setFlyState] = useState(null);

  // When splash starts fading, launch the flying logo
  useEffect(() => {
    if (splashState !== 'attaching') return;
    // Badge is still mounted during 'attaching' phase (condition updated in SplashScreen)
    // Read rects after paint so both source and dest are in the DOM
    requestAnimationFrame(() => {
      const src = splashLogoRef.current?.getBoundingClientRect();
      const dst = headerLogoRef.current?.getBoundingClientRect();
      if (!src || !dst || src.width === 0) return;
      setFlyState({ x: src.left, y: src.top, w: src.width, h: src.height,
                    tx: dst.left, ty: dst.top, tw: dst.width, th: dst.height });
    });
  }, [splashState]);

  const handleFlyDone = useCallback(() => setFlyState(null), []);

  return (
      <div className={`App${matrixMode ? ' matrix-mode-active' : ''}`}>
        <PageTracker />

        {/* ── Easter Egg: Konami Code Overlay ── */}
        {konamiActive && (
          <div className="konami-overlay" aria-hidden="true">
            <div className="konami-crt">
              <div className="konami-scanlines" />
              <p className="konami-line konami-line--1">▓▓▓ HIDDEN EASTER EGG FOUND ▓▓▓</p>
              <p className="konami-line konami-line--2">YOU DID IT</p>
              <p className="konami-line konami-line--3">NEXT CLUE: type "imatrix"</p>
              <p className="konami-line konami-line--4">or click the footer logo 5 times</p>
              <p className="konami-line konami-line--5 konami-big">3000+ Businesses</p>
              <p className="konami-line konami-line--6">grow since 2006 &nbsp;✨</p>
              <p className="konami-line konami-line--7 konami-sub">[ press any key to continue ]</p>
            </div>
          </div>
        )}

        {/* ── Easter Egg: Matrix Mode Toast ── */}
        {matrixMode && (
          <>
            <div className="matrix-toast" aria-hidden="true">
              <span className="matrix-toast__dot" />
              Matrix mode: ON 🟢
            </div>

            <div className="matrix-egg-map" aria-live="polite">
              <div className="matrix-egg-map__header">Easter Egg Map</div>
              <ul>
                <li><span className="matrix-egg-map__dot matrix-egg-map__dot--konami" /> Konami sequence</li>
                <li><span className="matrix-egg-map__dot matrix-egg-map__dot--matrix" /> Type “imatrix”</li>
                <li><span className="matrix-egg-map__dot matrix-egg-map__dot--rain" /> Click footer logo x5</li>
              </ul>
            </div>
          </>
        )}

        {/* ── Easter Egg: Matrix Digital Rain ── */}
        {matrixRain && <MatrixRain onDone={() => setMatrixRain(false)} />}

        {splashState !== 'completed' && (
          <SplashScreen splashState={splashState} setSplashState={setSplashState} logoRef={splashLogoRef} hideLogo={!!flyState} />
        )}

        {/* ── Guided Site Tour — commented out for later use ── */}
        {/* <SiteTour splashState={splashState} /> */}

        <Header splashState={splashState} logoRef={headerLogoRef} hideLogo={!!flyState} />

        {/* ── Flying logo — fixed, outside any fading container, GPU transform only ── */}
        {flyState && (() => {
          // Use transform: translate + scale only — zero layout triggers
          const scaleX = flyState.tw / flyState.w;
          const scaleY = flyState.th / flyState.h;
          return (
            <img
              key="flying-logo"
              src="/gvla-5star/gvla-5star.png"
              alt=""
              aria-hidden="true"
              style={{
                position: 'fixed',
                zIndex: 99999,
                top: 0,
                left: 0,
                width: flyState.w,
                height: flyState.h,
                objectFit: 'contain',
                pointerEvents: 'none',
                transformOrigin: '0 0',
                // Start at source position (no transition yet)
                transform: `translate(${flyState.x}px, ${flyState.y}px)`,
                transition: 'none',
              }}
              ref={el => {
                if (!el) return;
                // Double-rAF: guarantees browser painted the start position first
                requestAnimationFrame(() => {
                  requestAnimationFrame(() => {
                    el.style.transition = 'transform 1s cubic-bezier(0.16, 1, 0.3, 1)';
                    el.style.transform = `translate(${flyState.tx}px, ${flyState.ty}px) scale(${scaleX}, ${scaleY})`;
                    el.addEventListener('transitionend', handleFlyDone, { once: true });
                  });
                });
              }}
            />
          );
        })()}
     
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<Home splashState={splashState} />} />
        <Route path="/imatrix" element={<IMatrixComponent />} />
        <Route path="/tallyprime" element={<TallyPrime />} />
        <Route path="/tallyshowcase" element={<TallyShowcase />} />
        <Route path="/timelines" element={<Timeline />} />
        <Route path="/contact" element={<Content_New />} />
        <Route path="/Client" element={<Client />} />
        <Route path="/AboutPage" element={<AboutPage />} />
        <Route path="/HardwareServices" element={<HardwareServices />} />
        <Route path="/SecuritySystem" element={<SecuritySystem />} />
        <Route path="/MobileDeveloper" element={<MobileDeveloper />} />
        <Route path="/WebDevelopment" element={<WebDevelopment />} />
        <Route path="/ServicePage" element={<ServicePage />} />
        <Route path="/ServerPage" element={<ServerPage />} />
        <Route path="/BIO" element={<ProductGrid />} />
        <Route path="/Antivirus" element={<Antivirus />} />
        <Route path="/autoBackupSoftware" element={<AutoBackupSoftware />} />
        <Route path="/PrinterSoftware" element={<PrinterSoftware />} />
        <Route path="/webcat" element={<WebList />} />
        <Route path="/e_data" element={<EWarehouse />} />
        <Route path="/pr" element={<PRPurchaseRequest />} />
        <Route path="/tr" element={<TransportWebApplication />} />
        <Route path="/career_pages" element={<CareerPage />} />
        <Route path="/whats-new" element={<WhatsNewPage />} />
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin/superadmin" element={<AdminGuard><SuperAdmin /></AdminGuard>} />
        <Route path="/admin/contactus" element={<AdminGuard><ContactAdmin /></AdminGuard>} />
        <Route path="/admin/pagehits" element={<AdminGuard><PageHitsAdmin /></AdminGuard>} />
        <Route path="/admin/whatsnew" element={<AdminGuard><WhatsNewAdmin /></AdminGuard>} />
        <Route path="/TallySoftware" element={<TallySoftware />} />
        <Route path="/TallyPrimeServer" element={<TallyPrimeServer />} />
        <Route path="/TallyAws" element={<TallyAws />} />
        <Route path="/WhatsAppBusiness" element={<WhatsAppBusiness />} />
        <Route path="/TallyPlatform" element={<TallyPlatform />} />
        <Route path="/TallyDefinition" element={<TallyDefinition />} />
        <Route path="/PrimeDetails" element={<PrimeDetails />} />
        <Route path="/TallyDetails" element={<TallyDetails />} />
        <Route path="/BizAnalyst" element={<BizAnalyst />} />
        <Route path="/OurService" element={<OurService/>} />
        <Route path="/InventoryManagement" element={<InventoryManagement/>} />
        <Route path="/ReportDesign" element={<ReportDesign/>} />
        <Route path="/PaymentManagement" element={<PaymentManagement/>} />
                <Route path="/Taxation" element={<Taxation/>} />
                <Route path="/InvoiceFeature" element={<InvoiceFeature/>} />
                <Route path="/BankReconciliation" element={<BankReconciliation/>} />
                <Route path="/ConnectedServices" element={<ConnectedServices/>} />
                <Route path="/plans" element={<Plans/>} />
                <Route path="/TallyPrimePlan" element={<TallyPrimePlan/>} />

                <Route path="/PlansAndPricing" element={<PlansAndPricing/>} />
                <Route path="/solutions" element={<SolutionsPage />} />
                <Route path="/TallyIra" element={<TallyIra />} />
                <Route path="/connect" element={<ConnectSocials />} />
                <Route path="/socials" element={<ConnectSocials />} />
                <Route path="/links" element={<ConnectSocials />} />



            


        <Route path="*" element={<NotFound />} />
      </Routes>
      <Footer onLogoClick={triggerMatrixRain} />

      {/* ── Global AI Chat Assistant ── */}
      <ChatBot />
    </div>
  );
}

export default function AppWrapper() {
  return (
    <Router>
      <App />
    </Router>
  );
}
